/**
 * Canonical coercion for Firestore date/time values read on the client.
 *
 * WHY THIS EXISTS
 * ---------------
 * The same instant reaches the frontend in several shapes depending on which writer produced the
 * document and when:
 *
 *   - a native Firestore `Timestamp` (has `.toDate()`)          — what live backend writers produce
 *   - a SERIALIZED Timestamp `{_seconds, _nanoseconds}`         — e.g. after JSON round-tripping
 *   - a raw epoch-millis `number`                                — `lastMessage.timestampNum`, `date`
 *   - an ISO 8601 `string`                                       — legacy writers (now unwired)
 *
 * A prod audit on 2026-07-21 measured all four in the wild on `conversations.lastMessage`:
 * `timestamp` held Timestamp x4131 and string x15; `date` held number x3845 and Timestamp x301.
 * Reading any of these with bare arithmetic produces `NaN` rather than an error — and `NaN`
 * compares false against every bound, so a bad value slips silently past range guards instead of
 * failing loudly.
 *
 * Logic is lifted verbatim from the long-standing coercer inside
 * `src/store/conversationsV2.js` (the chat room-list mapper), which has handled these shapes in
 * production for a long time. This module is the shared home for it.
 *
 * NOTE: `src/store/conversationsV2.js` still carries its own inline copy. Converging it onto this
 * module is deliberate follow-up work, kept separate because that file is the chat core and a
 * behaviour-identical refactor there deserves its own verification pass.
 */

/**
 * Coerce a Firestore-sourced time value to epoch milliseconds.
 *
 * @param {*} v Timestamp | {_seconds} | number | ISO string | null | undefined
 * @returns {number|null} epoch ms, or null when the value is absent or unparseable.
 *          Returns 0 for the epoch itself — callers that treat 0 as "missing" must
 *          continue to do their own falsy check (this function does not conflate the two).
 */
export function coerceToMs(v) {
  try {
    if (!v && v !== 0) return null;
    if (typeof v?.toDate === 'function') {
      // ONE deliberate deviation from the conversationsV2 original, which returned
      // `v.toDate().getTime()` unguarded. A real Firestore Timestamp always yields a valid Date,
      // so this changes nothing for real data — but a malformed object returned NaN, and NaN is
      // NOT nullish, so a caller writing `coerceToMs(x) ?? fallback` would silently receive NaN
      // instead of their fallback. Returning null keeps the documented `number|null` contract
      // honest. When conversationsV2 converges onto this module it inherits the fix.
      const ms = v.toDate().getTime();
      return isNaN(ms) ? null : ms;
    }
    if (typeof v === 'object' && typeof v._seconds === 'number') return v._seconds * 1000;
    const d = new Date(v);
    const ms = d.getTime();
    return isNaN(ms) ? null : ms;
  } catch (_) {
    return null;
  }
}

export default { coerceToMs };
